import { useState } from 'react';
import { useRoleManagement, AppRole } from '@/hooks/useRoleManagement';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserPlus, Shield, Users, Crown, Settings, Mail, Key } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { WorkspaceUserInvitations } from '@/components/workspace/WorkspaceUserInvitations';
import { WorkspaceModuleManagement } from '@/components/workspace/WorkspaceModuleManagement';
import AccessControlEnhanced from '@/pages/workspace/AccessControlEnhanced';

export function UserManagement() {
  const { userRoles, permissions, isLoading, assignRole, removeRole, toggleRoleStatus, isAssigning } = useRoleManagement();
  const { toast } = useToast();
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState<AppRole>('user');
  const [userId, setUserId] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const roleColors = {
    super_admin: 'destructive',
    admin: 'secondary',
    manager: 'default',
    analyst: 'outline',
    user: 'secondary',
    viewer: 'outline'
  } as const;

  const roleIcons = {
    super_admin: Crown,
    admin: Shield,
    manager: Settings,
    analyst: Users,
    user: Users,
    viewer: Users
  };

  const handleAssignRole = () => {
    if (!userId) {
      toast({
        title: "Error",
        description: "Please enter a user ID",
        variant: "destructive",
      });
      return;
    }

    assignRole({
      user_id: userId,
      role: selectedRole,
      expires_at: expiresAt || undefined
    });

    setShowAssignDialog(false);
    setUserId('');
    setExpiresAt('');
    setSelectedRole('user');
  };

  const roleStats = userRoles.reduce((acc, role) => {
    acc[role.role] = (acc[role.role] || 0) + 1;
    return acc;
  }, {} as Record<AppRole, number>);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="h-4 bg-muted animate-pulse rounded w-20"></div>
                <div className="h-4 w-4 bg-muted animate-pulse rounded"></div>
              </CardHeader>
              <CardContent>
                <div className="h-7 bg-muted animate-pulse rounded w-8 mb-1"></div>
                <div className="h-3 bg-muted animate-pulse rounded w-16"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">User Management & Access Control</h2>
          <p className="text-muted-foreground">
            Manage users, roles, permissions, workspace access, and module availability
          </p>
        </div>
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogTrigger asChild>
            <Button>
              <UserPlus className="mr-2 h-4 w-4" />
              Assign Role
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign User Role</DialogTitle>
              <DialogDescription>
                Assign a role to a user. Enter the user ID and select the appropriate role.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="userId">User ID</Label>
                <Input
                  id="userId"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="Enter user UUID"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={selectedRole} onValueChange={(value: AppRole) => setSelectedRole(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="analyst">Analyst</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="expiresAt">Expires At (Optional)</Label>
                <Input
                  id="expiresAt"
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAssignRole} disabled={isAssigning}>
                {isAssigning ? "Assigning..." : "Assign Role"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs for User Management, Invitations, and Access Control */}
      <Tabs defaultValue="users" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span>Users & Roles</span>
            <Badge variant="secondary" className="ml-1 text-xs">
              System
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="invitations" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            <span>Invitations</span>
          </TabsTrigger>
          <TabsTrigger value="access-control" className="flex items-center gap-2">
            <Key className="h-4 w-4" />
            <span>Plans & Modules</span>
            <Badge variant="outline" className="ml-1 text-xs">
              Workspace
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-6">
      {/* Role Statistics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {(['super_admin', 'admin', 'manager', 'user'] as AppRole[]).map((role) => {
          const Icon = roleIcons[role];
          return (
            <Card key={role}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium capitalize">
                  {role.replace('_', ' ')}
                </CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{roleStats[role] || 0}</div>
                <p className="text-xs text-muted-foreground">active users</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* User Roles Table */}
      <Card>
        <CardHeader>
          <CardTitle>User Roles</CardTitle>
          <CardDescription>
            View and manage all user role assignments
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User ID</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Assigned At</TableHead>
                <TableHead>Expires At</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {userRoles.map((userRole) => (
                <TableRow key={userRole.id}>
                  <TableCell className="font-mono text-sm">
                    {userRole.user_id.slice(0, 8)}...
                  </TableCell>
                  <TableCell>
                    <Badge variant={roleColors[userRole.role] as any}>
                      {userRole.role.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {new Date(userRole.assigned_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {userRole.expires_at 
                      ? new Date(userRole.expires_at).toLocaleDateString()
                      : 'Never'
                    }
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={userRole.is_active}
                      onCheckedChange={(checked) => 
                        toggleRoleStatus({ roleId: userRole.id, isActive: checked })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeRole(userRole.id)}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Permissions Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Role Permissions</CardTitle>
          <CardDescription>
            Overview of permissions assigned to each role
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {(['super_admin', 'admin', 'manager', 'analyst', 'user', 'viewer'] as AppRole[]).map((role) => {
              const rolePermissions = permissions.filter(p => p.role === role);
              return (
                <div key={role} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold capitalize">{role.replace('_', ' ')}</h4>
                    <Badge variant={roleColors[role] as any}>
                      {rolePermissions.length} permissions
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {rolePermissions.map((permission) => (
                      <Badge key={permission.id} variant="outline" className="text-xs">
                        {permission.permission_type} {permission.resource}
                      </Badge>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="invitations">
          <WorkspaceUserInvitations />
        </TabsContent>

        <TabsContent value="access-control" className="space-y-6">
          <AccessControlEnhanced />
        </TabsContent>
      </Tabs>
    </div>
  );
}